import GlobalStyles from '@mui/joy/GlobalStyles';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import Sheet from '@mui/joy/Sheet';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import Divider from '@mui/joy/Divider';

import {
  ShieldCheck, Exam, ChartBar, Smiley, House, BookOpen, Sparkle, Megaphone, TelegramLogo,
  ClipboardText, Trophy, Path, FileText, Moon, Sun, PencilSimple, SignOut,
  Storefront, SidebarSimple, Rocket
} from '@phosphor-icons/react';

import { closeSidebar } from '../../utils';
import { useLocation, Link as RouterLink } from 'react-router';
import { Avatar, Box, Dropdown, IconButton, Menu, MenuButton, MenuItem, useColorScheme } from '@mui/joy';
import { useContext, useState } from 'react';
import { LayoutContext } from '../../context';
import { API_URL } from '../../api';

const RAIL_W = 56;
const EXPANDED_W = 220;

function NavItem({ icon: Icon, label, active, to, onClick, expanded, size = 20 }) {
  // `to` makes ListItemButton render as <a> via RouterLink → Cmd/middle-click,
  // right-click "Open in new tab" работают нативно. closeSidebar дополнительно
  // вызывается onClick'е чтобы на мобиле сайдбар закрывался при тапе.
  const handleClick = (e) => {
    closeSidebar();
    if (onClick) onClick(e);
  };
  const linkProps = to
    ? { component: RouterLink, to, replace: true, onClick: handleClick }
    : { onClick: handleClick };
  const button = (
    <ListItem sx={{ p: 0 }}>
      <ListItemButton
        {...linkProps}
        sx={{
          minHeight: 40,
          justifyContent: expanded ? 'flex-start' : 'center',
          px: expanded ? 1.5 : 0,
          gap: 1.5,
          borderRadius: '8px',
          color: active ? '#DA7756' : 'text.secondary',
          textDecoration: 'none',
          bgcolor: active
            ? (theme) => theme.palette.mode === 'dark' ? 'rgba(218,119,86,0.12)' : 'rgba(218,119,86,0.08)'
            : 'transparent',
          borderLeft: active ? '3px solid #DA7756' : '3px solid transparent',
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          '&:hover': {
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
          },
        }}
      >
        <Icon size={size} weight={active ? 'fill' : 'regular'} style={{ flexShrink: 0 }} />
        {expanded && (
          <ListItemContent>
            <Typography level="body-sm" sx={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>{label}</Typography>
          </ListItemContent>
        )}
      </ListItemButton>
    </ListItem>
  );

  if (expanded) return button;
  return (
    <Tooltip title={label} placement="right" arrow variant="outlined" size="sm">
      {button}
    </Tooltip>
  );
}

export default function Sidebar() {
  const location = useLocation();
  const { user, logout, setModalAccountOpen, openUpgradeModal } = useContext(LayoutContext);
  const { mode, setMode } = useColorScheme();
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem('sidebar-expanded') === 'true'; } catch { return false; }
  });

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    try { localStorage.setItem('sidebar-expanded', String(next)); } catch {}
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { icon: House, label: 'Главная', path: '/' },
    { icon: Path, label: 'Учебные планы', path: '/plans' },
    { icon: BookOpen, label: 'Библиотека', path: '/modules' },
    { icon: FileText, label: 'Документы', path: '/documents' },
    { icon: Exam, label: 'Тесты', path: '/tests' },
    { icon: ClipboardText, label: 'Пробники', path: '/mock-exams' },
    { icon: ChartBar, label: 'Статистика', path: '/stats' },
    { icon: Trophy, label: 'Рейтинг', path: '/leaderboard' },
  ];

  const adminItems = [
    { icon: ShieldCheck, label: 'Админка', path: '/admin' },
    { icon: Smiley, label: 'Пользователи', path: '/users' },
    { icon: Sparkle, label: 'PDF Генератор', path: '/generator' },
    { icon: ClipboardText, label: 'Задачи', path: '/tasks' },
  ];

  const bottomItems = [
    { icon: Storefront, label: 'Продукты', path: '/fanki' },
    { icon: Sparkle, label: 'Обновления', path: '/updates' },
  ];

  const width = expanded ? EXPANDED_W : RAIL_W;

  return (
    <Sheet
      className="Sidebar"
      sx={{
        position: { xs: 'fixed', lg: 'sticky' },
        transform: {
          xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))',
          lg: 'none',
        },
        transition: 'transform 0.4s, width 0.25s ease',
        zIndex: 1002,
        height: { xs: '100dvh', sm: '-webkit-fill-available' },
        width,
        top: 0,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: expanded ? 'stretch' : 'center',
        py: 1.5,
        px: expanded ? 1.5 : 0.5,
        gap: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        borderRadius: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <GlobalStyles styles={() => ({ ':root': { '--Sidebar-width': `${width}px` } })} />

      {/* Mobile overlay */}
      <Box
        className="Sidebar-overlay"
        sx={{
          display: { xs: 'block', lg: 'none' },
          position: 'fixed', zIndex: 9998, top: 0, left: 0,
          width: '100vw', height: '100vh',
          opacity: 'var(--SideNavigation-slideIn)',
          backgroundColor: 'var(--joy-palette-background-backdrop)',
          transition: 'opacity 0.4s',
          transform: {
            xs: 'translateX(calc(120% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))',
            lg: 'translateX(-120%)',
          },
        }}
        onClick={() => closeSidebar()}
      />

      {/* Logo + toggle row */}
      {expanded ? (
        <Box sx={{
          display: 'flex', alignItems: 'center', mb: 1.5, flexShrink: 0,
          justifyContent: 'space-between', px: 0.5,
        }}>
          <Typography level="title-md" sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden' }}>
            APEX <Box component="span" sx={{ color: '#DA7756' }}>MED</Box>
          </Typography>
          <Tooltip title="Свернуть" placement="right" arrow variant="outlined" size="sm">
            <IconButton variant="plain" color="neutral" size="sm" onClick={toggle} sx={{ borderRadius: '8px' }}>
              <SidebarSimple size={18} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box sx={{ mb: 0.5, flexShrink: 0 }}>
          <Tooltip title="Развернуть" placement="right" arrow variant="outlined" size="sm">
            <IconButton variant="plain" color="neutral" size="sm" onClick={toggle} sx={{ borderRadius: '8px' }}>
              <SidebarSimple size={18} weight="regular" style={{ transform: 'rotate(180deg)' }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Main nav */}
      <List size="sm" sx={{ gap: 0.25, width: '100%', flex: 1, '--ListItem-radius': '8px' }}>
        {navItems.map((item) => (
          <NavItem
            key={item.path}
            icon={item.icon}
            label={item.label}
            active={isActive(item.path)}
            to={item.path}
            expanded={expanded}
          />
        ))}

        {user?.role === 'admin' && (
          <>
            <Divider sx={{ my: 0.5 }} />
            {adminItems.map((item) => (
              <NavItem
                key={item.path}
                icon={item.icon}
                label={item.label}
                active={isActive(item.path)}
                to={item.path}
                expanded={expanded}
              />
            ))}
          </>
        )}

        <Divider sx={{ my: 0.5 }} />

        {/* «Улучшить тариф» — всем кроме тех у кого уже полный grand_* план.
            free / plan1 / Bezlimit / QBank-only → могут апгрейдиться до Grand Slam.
            grand_1m/3m/6m/12m → у них уже всё, кнопка не нужна. */}
        {user?.rate?.plan_type && !user.rate.plan_type.startsWith('grand_') && (
          <ListItem sx={{ p: 0 }}>
            <ListItemButton
              onClick={() => { openUpgradeModal?.('Оформи полный доступ к платформе'); closeSidebar(); }}
              sx={{
                minHeight: 40,
                justifyContent: expanded ? 'flex-start' : 'center',
                px: expanded ? 1.5 : 0,
                gap: 1.5,
                borderRadius: '8px',
                color: '#DA7756',
                border: '1px solid',
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(218,119,86,0.4)' : 'rgba(184,76,49,0.35)',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(218,119,86,0.08)' : 'rgba(218,119,86,0.06)',
                '&:hover': {
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(218,119,86,0.18)' : 'rgba(218,119,86,0.12)',
                },
              }}
            >
              <Rocket size={20} weight="fill" style={{ flexShrink: 0 }} />
              {expanded && (
                <ListItemContent>
                  <Typography level="body-sm" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', color: '#DA7756', fontWeight: 600 }}>
                    Улучшить тариф
                  </Typography>
                </ListItemContent>
              )}
            </ListItemButton>
          </ListItem>
        )}

        {bottomItems.map((item) => (
          <NavItem
            key={item.path}
            icon={item.icon}
            label={item.label}
            active={isActive(item.path)}
            to={item.path}
            expanded={expanded}
          />
        ))}

        <NavItem
          icon={Megaphone}
          label="Обратная связь"
          active={false}
          onClick={() => window.open('https://forms.gle/1dYeCVw8q9opR3en8', '_blank')}
          expanded={expanded}
        />

        <NavItem
          icon={TelegramLogo}
          label="Поддержка"
          active={false}
          onClick={() => window.open('https://t.me/ashurovmed', '_blank')}
          expanded={expanded}
        />
      </List>

      {/* Bottom: theme toggle + profile */}
      <Box sx={{
        display: 'flex', flexDirection: 'column', gap: 1, mt: 1, flexShrink: 0,
        alignItems: expanded ? 'stretch' : 'center',
      }}>
        {/* Theme toggle */}
        {expanded ? (
          <ListItemButton
            onClick={() => setMode(mode === 'light' ? 'dark' : 'light')}
            sx={{
              minHeight: 40, gap: 1.5, px: 1.5, borderRadius: '8px',
              color: 'text.secondary',
              '&:hover': {
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            {mode === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            <Typography level="body-sm">{mode === 'light' ? 'Тёмная тема' : 'Светлая тема'}</Typography>
          </ListItemButton>
        ) : (
          <Tooltip title={mode === 'light' ? 'Тёмная тема' : 'Светлая тема'} placement="right" arrow variant="outlined" size="sm">
            <IconButton
              variant="plain" color="neutral" size="sm"
              onClick={() => setMode(mode === 'light' ? 'dark' : 'light')}
              sx={{ borderRadius: '8px' }}
            >
              {mode === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </IconButton>
          </Tooltip>
        )}

        {/* Profile */}
        <Dropdown>
          {expanded ? (
            <MenuButton
              slots={{ root: ListItemButton }}
              slotProps={{
                root: {
                  sx: {
                    minHeight: 44, gap: 1.5, px: 1.5, borderRadius: '8px',
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    },
                  },
                },
              }}
            >
              <Avatar
                src={user?.avatar ? `${API_URL}${user.avatar}?${Date.now()}` : '/smile.png'}
                sx={{ width: 28, height: 28 }}
              />
              <Typography level="body-sm" sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.first_name} {user?.last_name}
              </Typography>
            </MenuButton>
          ) : (
            <Tooltip title={`${user?.first_name || ''} ${user?.last_name || ''}`} placement="right" arrow variant="outlined" size="sm">
              <MenuButton
                slots={{ root: IconButton }}
                slotProps={{
                  root: {
                    variant: 'plain', color: 'neutral', size: 'sm',
                    sx: { borderRadius: '50%', p: 0 },
                  },
                }}
              >
                <Avatar
                  src={user?.avatar ? `${API_URL}${user.avatar}?${Date.now()}` : '/smile.png'}
                  sx={{ width: 32, height: 32 }}
                />
              </MenuButton>
            </Tooltip>
          )}
          <Menu size="sm" placement="right-end" sx={{ minWidth: 200, '--ListItemDecorator-size': '24px', zIndex: 9996 }}>
            <MenuItem onClick={() => setModalAccountOpen()}>
              <PencilSimple size={18} />
              Редактировать профиль
            </MenuItem>
            <MenuItem color="danger" onClick={logout}>
              <SignOut size={18} />
              Выйти
            </MenuItem>
          </Menu>
        </Dropdown>
      </Box>
    </Sheet>
  );
}
